import { Month } from "@/types/dto/search-filters/month-picker";
import { Locale } from "@/i18n/routing";
import {
  mapApiValueToDisplay,
  getFieldConfig,
  getFieldDisplayName
} from "@/config/unified-search-config";

export function searchFilterFactory(data: any = {}) {
  return {
    displayName: data.displayName || "",
    value: data.value || "",
    count: data.count || 0
  };
}

export function searchFiltersFactory(data: any = {}) {
  const fieldName = data.fieldName || "";
  let options = (data.options || []).map(searchFilterFactory);

  // Get field configuration from unified config
  const fieldConfig = getFieldConfig(fieldName);

  if (fieldConfig) {
    // Apply value mappings if configured
    if (fieldConfig.valueMap) {
      // For ActivityLevel and similar fields: map what we can, keep the rest
      if (fieldName === "ActivityLevel") {
        options = options.map((opt: any) => ({
          ...opt,
          displayName: mapApiValueToDisplay(fieldName, opt.value), // Maps if exists, otherwise returns original
          value: opt.value
        }));
      } else {
        // For TourStyle, Badges, Offers: filter to only include options that have mappings
        options = options
          .filter((opt: any) => fieldConfig.valueMap![opt.value] !== undefined)
          .map((opt: any) => ({
            ...opt,
            displayName: mapApiValueToDisplay(fieldName, opt.value),
            value: opt.value
          }));
      }
    }

    // Apply sorting if configured
    if (fieldConfig.requiresSort) {
      if (fieldConfig.sortBy === "numeric") {
        options.sort((a: any, b: any) => Number(a.value) - Number(b.value));
      } else if (fieldConfig.sortBy === "alphabetic") {
        options.sort((a: any, b: any) => a.displayName.localeCompare(b.displayName));
      }
    }
  }

  // Special handling for CustomerReview (applies regardless of fieldConfig)
  if (fieldName === "CustomerReview") {
    options = options.filter((opt: any) => {
      const displayNum = Number(opt.displayName);
      return (
        !Number.isNaN(displayNum) &&
        displayNum >= 0 &&
        displayNum <= 5 &&
        opt.count > 0
      );
    });
  }

  return {
    id: data.id || "",
    displayName: getFieldDisplayName(fieldName) || data.displayName || "",
    fieldName,
    options
  };
}

function getMonthLabel(monthNum: number, locale: Locale) {
  const date = new Date(2000, monthNum - 1, 1); // Use a dummy date, only month matters

  return date.toLocaleString(locale, { month: "short" });
}

export function monthFactory(date: any, locale: Locale): Month {
  const regex = /^(0[1-9]|1[0-2])\/\d{4}$/;
  if (typeof date !== "string" || !regex.test(date)) throw new Error("Unable to parse date");

  const dateSplit = date.split("/");
  const month = dateSplit[0];
  const year = dateSplit[1];

  return {
    id: `${year}${month}`,
    month: parseInt(month, 10),
    year: parseInt(year, 10),
    label: getMonthLabel(parseInt(month, 10), locale).toUpperCase()
  };
}

export function getMonthObjects(currentMonth: Month, minMonth: Month, maxMonth: Month, locale: Locale): Month[] {
  const months: Month[] = [];
  let month: Month = currentMonth;
  while (month.year <= maxMonth.year) {
    months.push({
      ...month,
      disabled: (month.month < minMonth.month && month.year <= minMonth.year)
        || (month.year === maxMonth.year && month.month > maxMonth.month)
    });
    const nextMonth = month.month === 12 ? 1 : month.month + 1;
    const nextYear = month.month === 12 ? month.year + 1 : month.year;
    const nextId = `${nextYear}${nextMonth.toString().padStart(2, "0")}`;
    const nextLabel = getMonthLabel(nextMonth, locale).toUpperCase();
    month = {
      month: nextMonth,
      year: nextYear,
      label: nextLabel,
      id: nextId
    };
  }
  return months;
}

// breaks months up into years
export function groupMonthsByYear(months: Month[]): Month[][] {
  const monthsByYear = months.reduce((acc, cur) => {
    const currentYear = cur.year.toString();
    if (!acc[currentYear]) {
      acc[currentYear] = [];
    }
    acc[currentYear].push(cur);
    return acc;
  }, {} as { [year: string]: Month[] });

  return Object.values(monthsByYear);
}

// export function boundMonthRange(currentDate: Date, minMonth: string, )